/**
 * CSS-строка для пузырей сообщений (STEP8 эт.2, доводка Момо 18:10).
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ CSS, а не inline-объекты, как в остальной ленте:
 * настоящий «хвостик» пузыря в стиле Telegram — псевдоэлемент (::after),
 * а inline-стиль псевдоэлементы не умеет в принципе. Зуко это обошёл
 * уменьшением одного угла (borderBottomLeftRadius: 4) — угол скруглялся,
 * но хвостика не появлялось, и пузырь читался как простой прямоугольник.
 * Отсюда и вывод Антона 18:07: «пузыри явно недоделанные».
 *
 * Цвета подставляются из theme.ts — литеральных hex здесь быть не должно.
 */
import { color, font, radius } from "./theme";

export const bubbleCss = `
.alg-bubble {
  position: relative;
  border-radius: ${radius.bubble}px;
  padding: 6px 12px 4px;
  color: ${color.text};
  /* Место под время в правом нижнем углу: без резерва последняя строка
     текста заезжает под часы. Телеграм делает ровно так же. */
  padding-right: 64px;
  min-width: 88px;
}

/* ЧУЖОЙ пузырь — своя ступень кости, слева. */
.alg-bubble.in { background: ${color.bubbleIn}; }
/* СВОЙ — тёплый градиент в сторону красного (постановка Антона 16:21). */
.alg-bubble.out {
  background: linear-gradient(180deg, ${color.bubbleOut} 0%, ${color.bubbleOutTo} 100%);
}

/* ФОРМА ГРУППЫ. В Telegram пузыри одного автора срастаются: у среднего
   сообщения угол со стороны автора почти прямой, у последнего — хвостик.
   Без этого группа выглядит как набор одинаковых плашек. */
.alg-bubble.in.mid  { border-top-left-radius: 4px; border-bottom-left-radius: 4px; }
.alg-bubble.in.first { border-bottom-left-radius: 4px; }
.alg-bubble.in.last  { border-top-left-radius: 4px; }
.alg-bubble.out.mid  { border-top-right-radius: 4px; border-bottom-right-radius: 4px; }
.alg-bubble.out.first { border-bottom-right-radius: 4px; }
.alg-bubble.out.last  { border-top-right-radius: 4px; }

/* ХВОСТИК у последнего в группе — настоящий, псевдоэлементом.
   Рисуем треугольник границами: он наследует цвет пузыря и не требует
   ни картинки, ни лишнего div внутри потока текста. */
.alg-bubble.last::after {
  content: "";
  position: absolute;
  bottom: 0;
  width: 0;
  height: 0;
  border-style: solid;
}
.alg-bubble.in.last::after {
  left: -6px;
  border-width: 0 0 10px 7px;
  border-color: transparent transparent ${color.bubbleIn} transparent;
}
/* У своего пузыря хвостик берёт НИЖНЮЮ ступень градиента: он растёт из
   низа пузыря, и верхний тон дал бы заметный стык. */
.alg-bubble.out.last::after {
  right: -6px;
  border-width: 0 7px 10px 0;
  border-color: transparent transparent ${color.bubbleOutTo} transparent;
}

/* ВРЕМЯ внутри пузыря, правый нижний угол. Абсолютом, а не float:
   float после блочного тела уводил часы на отдельную строку и раздувал
   пузырь по высоте на каждом сообщении. */
.alg-bubble-time {
  position: absolute;
  right: 10px;
  bottom: 4px;
  font-size: ${font.tiny}px;
  color: ${color.textMuted};
  line-height: 1;
  user-select: none;
  white-space: nowrap;
}

/* НАДГРОБИЕ удалённого сообщения (Антон, 2026-09-21 09:29).

   ТРЕБОВАНИЕ: «визуально отличается от обычного сообщения, НО остаётся
   в правильной стороне ленты и сохраняет время». Поэтому класс tombstone
   добавляется К "alg-bubble in|out", а не заменяет их: сторона, ряд, отступы
   и блок времени (.alg-bubble-time) остаются ровно теми же.

   Что меняем и почему:
    - фон убираем в прозрачный, вместо него пунктирная рамка — «пустой баллон»
      из постановки читается именно как пустота, а не как ещё одно сообщение.
      Градиент своего пузыря гасим явно ("background: transparent" поверх
      "linear-gradient"), иначе своё удалённое осталось бы ярко-красным;
    - текст курсивом и приглушённым цветом — это не слова автора, а пометка
      системы, и вес у неё обязан быть меньше любого живого сообщения;
    - хвостик (::after) убираем: он рисуется сплошным цветом пузыря и рядом
      с прозрачным фоном выглядел бы оторванным треугольником в воздухе;
    - min-width снижаем: у надгробия нет тела, и штатные 88px давали бы
      неоправданно широкую плашку. Резерв под часы (padding-right) ОСТАЁТСЯ:
      время на месте, и без резерва фраза заехала бы под него. */
.alg-bubble.tombstone,
.alg-bubble.out.tombstone {
  background: transparent;
  border: 1px dashed ${color.border};
  color: ${color.textMuted};
  font-style: italic;
  min-width: 0;
}
.alg-bubble.tombstone::after { display: none; }

/* ПОДСВЕТКА найденного сообщения (прыжок по реплаю, поиск).
   Рамкой и тенью, а НЕ фоном: фон у своего пузыря — градиент из inline-
   стиля, и подмена фона стирала его безвозвратно (React не перерисует
   элемент, у которого не менялись пропсы). */
.alg-bubble.is-highlight {
  box-shadow: 0 0 0 2px ${color.badgeHighlight}, 0 0 0 6px ${color.highlight};
  transition: box-shadow 1.2s ease-out;
}

/* УПОМИНАНИЕ МЕНЯ — кайма слева, фон пузыря не трогаем (иначе теряется
   различие свой/чужой, которое и есть главный признак в ленте). */
.alg-bubble.mentions-me {
  box-shadow: inset 3px 0 0 ${color.badgeHighlight};
}
`;

/**
 * ЧИТАТЕЛИ — маленькие аватары под пузырём (решение Антона 18:45).
 *
 * Стопкой с наложением, как в Telegram/Slack: 5 лиц занимают ширину
 * примерно трёх. Порядок наложения — левое поверх правого (direction:
 * rtl у контейнера), иначе последний аватар перекрывает предыдущие и
 * получается «лестница» вместо стопки.
 */
export const readAvatarsCss = `
.alg-readers {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 0;
  margin-top: 2px;
  user-select: none;
}
.alg-reader {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 8px;
  font-weight: 700;
  color: ${color.textOnAccent};
  /* Кайма цветом ленты: аватары наезжают друг на друга, и без каймы
     соседние цвета сливаются в пятно. */
  box-shadow: 0 0 0 1.5px ${color.feedBg};
  margin-right: -5px;
  flex: 0 0 16px;
}
.alg-reader:first-child { margin-right: 0; }
/* Счётчик «+N», когда читателей больше, чем показываем. */
.alg-readers-more {
  font-size: ${font.tiny}px;
  color: ${color.textMuted};
  margin-right: 6px;
}
`;
